import { APP_NAME, APP_TAGLINE, BRAND_LOGO, BRAND_SKY } from "@/lib/brand";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, MailWarning, Loader2 } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="accept-invite-page">
      {/* Brand panel */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(37,99,235,0.88), rgba(59,130,246,0.42)), url(${BRAND_SKY})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 inline-flex items-center bg-white/95 rounded-xl px-3.5 py-2 shadow-sm w-fit">
          <img src={BRAND_LOGO} alt={APP_NAME} className="h-[31px] w-auto object-contain" />
        </div>
        <div className="relative z-10 fade-in-up">
          <p className="overline text-white/80">You're invited</p>
          <h1 className="mt-4 text-4xl xl:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-md">{APP_TAGLINE}</h1>
          <p className="mt-5 text-white/85 max-w-md leading-relaxed">
            Set up your account in a moment and jump straight into your workspace — projects, workstreams and ERP handover, clearly in one place.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/70">© {new Date().getFullYear()} {APP_NAME} · {APP_TAGLINE}</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <img src={BRAND_LOGO} alt={APP_NAME} className="h-8 w-auto object-contain" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", password: "" });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    if (!token) return setError("Missing invitation token. Ask your Workspace Admin for a new invitation link.");
    api.get(`/auth/invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This invitation link is invalid or has expired."));
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, ...form, accept_privacy: acceptPrivacy, accept_terms: acceptTerms });
      setUser(data.user);
      toast.success(`Welcome to ${invite.workspace}`);
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="invite-error-state">
          <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 grid place-items-center">
            <MailWarning className="h-6 w-6 text-amber-600" />
          </div>
          <h1 className="mt-4 text-xl font-bold tracking-tight text-slate-900">Invitation unavailable</h1>
          <p className="mt-2 text-sm text-slate-500" data-testid="invite-error">{error}</p>
          <Button variant="outline" className="mt-6" onClick={() => navigate("/login")} data-testid="invite-back-login">Back to sign in</Button>
        </div>
      </Shell>
    );
  }

  if (!invite) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="invite-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking your invitation…
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="overline">Activate your account</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Join {invite.workspace}</h1>
      <p className="mt-2 text-sm text-slate-500">
        You were invited as <strong className="capitalize">{invite.role.replace(/_/g, " ")}</strong> with <span className="font-mono text-slate-700">{invite.email}</span>.
      </p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div>
          <Label className="text-xs font-medium text-slate-600">Your name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="mt-1" data-testid="invite-name-input" />
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">Choose a password (min 8 characters)</Label>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="mt-1" data-testid="invite-password-input" />
        </div>
        <div className="space-y-2 pt-1">
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <Checkbox checked={acceptPrivacy} onCheckedChange={(v) => setAcceptPrivacy(!!v)} className="mt-0.5" data-testid="invite-accept-privacy" />
            <span>I have read and accept the <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline">Privacy Policy</a>.</span>
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <Checkbox checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" data-testid="invite-accept-terms" />
            <span>I accept the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline">Terms of Use</a>.</span>
          </label>
        </div>
        <Button
          onClick={submit}
          disabled={submitting || !form.name.trim() || form.password.length < 8 || !acceptPrivacy}
          className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] h-10"
          data-testid="invite-accept-btn"
        >
          {submitting ? "Creating your account…" : "Create my account"}
          {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </div>

      <div className="mt-6 text-xs text-slate-500">
        <a href="/privacy" className="hover:underline hover:text-slate-800">Privacy Policy</a>
        <span className="mx-2">·</span>
        <a href="/terms" className="hover:underline hover:text-slate-800">Terms of Use</a>
      </div>
    </Shell>
  );
}
